import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";
import api from "../lib/api";
import { ShieldCheck, AlertTriangle } from "lucide-react";

/**
 * Non-dismissable quarterly HPCSA re-attestation. Shown when /auth/me returns
 * `hpcsa_attestation_due: true`. Clicking "I confirm" posts to
 * /api/auth/attest-hpcsa which resets the 90-day clock.
 */
export default function HPCSAAttestationModal({ user, onConfirmed }) {
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/auth/attest-hpcsa");
      toast.success("Registration confirmed");
      onConfirmed?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not record attestation");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true}>
      <DialogContent
        className="max-w-md [&>button]:hidden"
        data-testid="hpcsa-attestation-modal"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#111827]" /> Quarterly registration check
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm text-slate-700">
          <p>
            Every 90 days we ask verified clinicians to confirm they still hold a current South African registration.
          </p>
          <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
            <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Registration on file</div>
            <div className="font-mono text-sm mt-0.5">{user?.hpcsa_number || "—"}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">Registered name: {user?.full_name}</div>
          </div>
          <div className="p-3 rounded-md bg-amber-50 border border-amber-200 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-900">
              By clicking <strong>I confirm</strong>, you attest that this registration number is current, that you are the registered practitioner / student named above, and that the account is not being shared. False attestations may result in immediate suspension.
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2 mt-4">
          <Button
            onClick={submit}
            disabled={loading}
            className="bg-[#0B5FFF] hover:bg-[#0A54E0] w-full"
            data-testid="hpcsa-attestation-confirm"
          >
            {loading ? "Confirming..." : "I confirm I still hold this registration"}
          </Button>
          <a
            href="mailto:rapidcaresasupport@gmail.com?subject=Registration%20no%20longer%20valid"
            className="text-xs text-slate-500 text-center hover:text-rose-700 underline"
            data-testid="hpcsa-attestation-contact"
          >
            I no longer hold this registration → contact support
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}
